/* eslint-disable @typescript-eslint/prefer-for-of */
"use client"

import type { VariantProps } from "class-variance-authority"
import { memo, useMemo } from "react"
import {
	AvatarFallback,
	AvatarImage,
	Avatar as RadixAvatar,
} from "@radix-ui/react-avatar"
import { cva } from "class-variance-authority"
import { IconUserFill18 as UserIcon } from "nucleo-ui-fill-18"

import { cn, USER_COLOR_PALETTE, UserColor } from "@productlane/lib/src/browser"

import type {
	AvatarFallbackProps,
	AvatarProps as RadixAvatarProps,
} from "@radix-ui/react-avatar"

// Force d=404 so Gravatar 404s on missing accounts instead of serving its blue default image, letting the initials fallback show
export function withGravatarDefault404(src?: string | null) {
	if (!src || !src.includes("gravatar.com/avatar/")) {
		return src ?? ""
	}
	const [base, query] = src.split("?")
	const params = new URLSearchParams(query)
	params.set("d", "404")
	return `${base}?${params.toString()}`
}

export function getUserInitial(userName: string) {
	const parts = userName ? userName.split(/[ -]/).filter(Boolean) : []
	if (parts.length === 0) {
		return ""
	}
	if (parts.length === 1) {
		return parts[0]!.slice(0, 2).toUpperCase()
	}
	let initials = ""
	for (let i = 0; i < parts.length; i += 1) {
		initials += parts[i]!.charAt(0)
	}
	if (initials.length > 2 && initials.search(/[A-Z]/) !== -1) {
		initials = initials.replace(/[a-z]+/g, "")
	}
	return initials.slice(0, 2).toUpperCase()
}

const AVATAR_COLORS = [
	UserColor.Purple,
	UserColor.Teal,
	UserColor.Green,
	UserColor.Yellow,
	UserColor.Orange,
	UserColor.Pink,
	UserColor.Red,
].map((color) => USER_COLOR_PALETTE[color].hex)

// Light from the top-left, a touch of shade at the bottom: sits on any background color
export const AVATAR_SHEEN =
	"radial-gradient(115% 115% at 30% 0%, rgba(255,255,255,0.24) 0%, rgba(255,255,255,0.06) 48%, rgba(0,0,0,0.14) 100%)"

export function getAvatarFallbackStyle(
	isTransparent: boolean,
	initialsColor?: string,
): React.CSSProperties {
	return {
		...(isTransparent ? {} : { backgroundImage: AVATAR_SHEEN }),
		...(initialsColor ? { backgroundColor: initialsColor } : {}),
	}
}

export function getAvatarColor(seed: string) {
	let hash = 0
	for (let i = 0; i < seed.length; i += 1) {
		hash = (hash << 5) - hash + seed.charCodeAt(i)
		hash |= 0
	}
	return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length]!
}

/**
 * A seed that lands on palette colour `slot`, so a short list (the portals of
 * one workspace) gets a different colour per entry instead of hash collisions.
 */
export function avatarSeedForSlot(slot: number): string {
	const target = AVATAR_COLORS[slot % AVATAR_COLORS.length]
	for (let k = 0; k < 1000; k += 1) {
		const seed = `slot-${k}`
		if (getAvatarColor(seed) === target) return seed
	}
	return `slot-${slot}`
}

const avatarVariants = cva(
	"inline-flex relative flex-none select-none items-center justify-center overflow-hidden bg-gray-100 dark:bg-gray-850 cursor-default",
	{
		variants: {
			// let's keep size and roundness of the avatars as separate props please
			size: {
				xxs: "h-4 w-4 min-h-4 min-w-4 text-[0.4rem]",
				"xxs+": "h-[18px] w-[18px] min-h-[18px] min-w-[18px] text-[0.45rem]",
				xs: "h-5 w-5 min-h-5 min-w-5 text-[0.5rem]",
				s: "h-6 w-6 !text-[0.5625rem]",
				sm: "h-8 w-8 !text-xxs",
				md: "h-10 w-10 !text-[0.8125rem]",
				lg: "h-12 w-12 text-md",
				xl: "h-14 w-14 text-xl",
			},
			variant: {
				default: "text-gray-500 dark:text-gray-600",
				solid: "bg-gray-500 dark:bg-gray-600 text-white",
				transparent: "bg-transparent dark:bg-transparent",
			},
			roundness: {
				full: "rounded-full",
				xl: "rounded-xl",
				lg: "rounded-lg",
				md: "rounded-md",
				sm: "rounded-sm",
			},
		},
		defaultVariants: {
			size: "md",
			variant: "default",
			roundness: "full",
		},
	},
)

export interface AvatarProps
	extends VariantProps<typeof avatarVariants>, RadixAvatarProps {
	image?: string | null
	fallbackImage?: string | null
	name: string
	colorSeed?: string | null
	disableColorHash?: boolean
	className?: string
	fallbackProps?: AvatarFallbackProps
	noReferrer?: boolean
	showAvatarFallback?: boolean
	isPortalFallback?: boolean
	onImageError?: () => void
}

function AvatarInner({
	image,
	fallbackImage,
	name,
	colorSeed,
	disableColorHash = false,
	className,
	size,
	roundness,
	variant,
	noReferrer,
	fallbackProps,
	showAvatarFallback,
	isPortalFallback = false,
	onImageError,
	...props
}: AvatarProps) {
	const initials = useMemo(() => getUserInitial(name), [name])
	const src = withGravatarDefault404(image)

	// Determine variant based on props
	const finalVariant =
		variant === "solid" && !image
			? "solid"
			: variant === "transparent"
				? "transparent"
				: "default"

	const initialsColor =
		!disableColorHash &&
		finalVariant !== "solid" &&
		!isPortalFallback &&
		!showAvatarFallback &&
		!fallbackImage &&
		initials
			? getAvatarColor(colorSeed || name)
			: undefined

	return (
		// Remount on src change: Radix keeps the load status per instance, so a stale
		// in-flight load can flip it to "loaded" after src empties and hide the fallback.
		<RadixAvatar
			key={src || "initials"}
			className={cn(
				avatarVariants({
					size,
					variant: finalVariant,
					roundness,
				}),
				className,
			)}
			{...props}
		>
			<AvatarImage
				referrerPolicy={noReferrer ? "no-referrer" : undefined}
				className="h-full w-full object-cover"
				src={src}
				alt="User Image"
				onError={onImageError}
			/>
			<AvatarFallback
				{...fallbackProps}
				style={{
					...getAvatarFallbackStyle(
						finalVariant === "transparent",
						initialsColor,
					),
					...fallbackProps?.style,
				}}
				className={cn(
					"flex h-full w-full items-center justify-center font-semibold leading-[initial] tracking-[0.01em]",
					{
						"bg-gray-500 text-white dark:bg-gray-600":
							finalVariant !== "solid" && !initialsColor && !isPortalFallback,
						"bg-[var(--portal-accent-color)]": isPortalFallback,
						"text-white": !!initialsColor,
					},
					fallbackProps?.className,
				)}
			>
				{fallbackImage ? (
					<AvatarImage
						className="h-full w-full object-cover"
						src={fallbackImage}
						alt="Fallback Image"
					/>
				) : showAvatarFallback ? (
					<UserIcon
						style={{
							display: "block",
							height: "1.2em",
							width: "1.2em",
							color: isPortalFallback
								? "var(--portal-button-text-color)"
								: undefined,
						}}
					/>
				) : (
					initials
				)}
			</AvatarFallback>
		</RadixAvatar>
	)
}

export const Avatar = memo(AvatarInner)
